import { Layout } from '@/components/Layout';
import { WIP_CATEGORIES } from '@/constants/categories';
import {
  SimulationCtx,
  SimulationProvider,
} from '@/contexts/Simulation/SimulationCtx';
import { CreateSimulationForm } from '@/features/CreateSimulationForm';
import { TSimulationMenuOptions } from '@/store/types';
import { Drawer, Tabs } from '@data-products-and-ai/react-components';
import { produce } from 'immer';
import { useContext } from 'react';
import { SimulationsList } from '../SimulationsList';
import { LeftMenu } from './LeftMenu';
import { TableView } from './TableView';
import { Loader } from '@/components/Loader';
import { FormView } from './FormView';
import usePostUsage from '@/hooks/usePostUsage';

/**
 * Maps and sorts the menu categories data
 * Filters out items with a negative order, sorts the remaining items in descending order, and maps them to a new format
 * @param {TopMenuCategories[]} data - The array of menu categories data
 */
const mapAndSortData = (data: TopMenuCategories[]) => {
  return data
    .filter((item) => item.order >= 0)
    .sort((a, b) => b.order - a.order) // sort in descending order
    .map((item) => ({
      // map to new format
      key: item.key, // converting index to string and adding 1 because array index is zero-based
      label: item.label,
    }));
};

type TopMenuCategories = {
  key: TSimulationMenuOptions;
  label: string;
  endpoint: string;
  order: number;
};

/**
 * ContentMenu Component
 * Renders the top menu tabs for the simulation content
 * Uses the simulation parameters from the SimulationCtx context
 * Handles the change of selected menu option
 */
const ContentMenu = () => {
  const MenuCategories: TopMenuCategories[] = [...WIP_CATEGORIES];
  const { simulationParams, setSimulationParams } = useContext(SimulationCtx);
  

  const handleChange = (value: TSimulationMenuOptions) => {
    setSimulationParams(
      produce((draft) => {
        draft.simulationOptionSelected = value;
      }),
    );
    console.log(value);
  };

  return (
    <div style={{ paddingTop: 20 }}>
      <Tabs
        fontSize={13}
        data={mapAndSortData([
          ...MenuCategories,
          {
            key: 'scenario_settings',
            label: 'Scenario Settings',
            endpoint: '',
            order: 1000,
          },
        ])}
        onChange={handleChange}
        selectedKey={simulationParams.simulationOptionSelected}
      />
    </div>
  );
};

/**
 * Content Component
 * Renders the appropriate content based on the simulation parameters
 * Uses the simulation parameters from the SimulationCtx context
 */
const Content = () => {
  const { simulationParams } = useContext(SimulationCtx);

  return (
    <>
      {!simulationParams.simulationForm ? (
        <>
          {simulationParams.simulationStarted ? (
            <TableView />
          ) : (
            <SimulationsList />
          )}
        </>
      ) : (
        <CreateSimulationForm />
      )}
    </>
  );
};

/**
 * SimulationContent Component
 * Renders the main simulation content with layout, drawer, and loader
 * Uses the simulation parameters from the SimulationCtx context
 * Handles the drawer state
 */
const SimulationContent = () => {
  const { simulationParams, setSimulationParams } = useContext(SimulationCtx);
  const handleDrawer: React.Dispatch<React.SetStateAction<boolean>> = (
    value: React.SetStateAction<boolean>,
  ) => {
    setSimulationParams((prevState) => ({
      ...prevState,
      drawerOpen:
        typeof value === 'function' ? value(prevState.drawerOpen) : value,
    }));

    console.log(simulationParams);
  };

  usePostUsage();

  return (
    <>
      {simulationParams.loaderMessage.toString()}
      {simulationParams.loaderMessage && (
        <Loader message={simulationParams.loaderMessage} />
      )}

      <Drawer
        closeOnClickOutside={true}
        showCloseButton={false}
        showBackdrop={true}
        width={500}
        fromRight={true}
        isOpen={simulationParams.drawerOpen}
        setIsOpen={handleDrawer}
      >
        <FormView />
      </Drawer>

      <Layout
        menu={<LeftMenu /* setDrawerOpen={setDrawerOpen} */ />}
        menuWidth={'300px'}
        menuCollapsed={false}
        content={<Content />}
        contentMenu={<ContentMenu />}
        contentMenuShow={simulationParams.simulationStarted}
      />
    </>
  );
};

const Simulation = () => {
  return (
    <>
      <SimulationProvider>
        <SimulationContent />
      </SimulationProvider>
    </>
  );
};

export default Simulation;
